import { el, clear } from "../util/dom";
import { fetchChartData } from "../chart/history";
import { renderChart, type AvgMode, type ChartElements } from "../chart/render";
import type { Bucket } from "../chart/window";
import { ApiError } from "../api/client";
import { isLoggedIn } from "../api/auth";
import { TIMEFRAME_MINUTES } from "../api/types";
import type { Timeframe } from "../api/types";

const TIMEFRAMES: Timeframe[] = ["5m", "15m", "1h", "4h"];

// Hard UI ceiling on periods (the number input's max), every timeframe,
// every entitled user. The backend's 48h window clamp was removed
// 2026-09-03 (beta feedback: 4h x 11 = 44h max was useless for multi-day
// review) -- wide windows are served from coarse rollups server-side, so
// this ceiling is purely about render sanity. 400 x 4h = ~66 days, which
// covers the full dataset today; raise it as history accumulates.
const HARD_MAX_PERIODS = 400;

// SW-6: the chart controls are client-local state, so they're persisted to
// localStorage and restored on load (the raw/Normalized scale already
// persists via displayMode.ts; server-persisted state is untouched).
// SW-9 added `timeZone`: an IANA id from TIMEZONES below, or absent for
// the device-local default.
const SETTINGS_KEY = "bitclock-chart-settings";

// SW-9: curated chart-timezone choices. "" = device local (the default);
// everything else is an IANA id passed straight into toLocale* options.
const TIMEZONES: ReadonlyArray<readonly [id: string, label: string]> = [
  ["", "Local"],
  ["UTC", "UTC"],
  ["America/New_York", "US Eastern"],
  ["America/Chicago", "US Central"],
  ["America/Denver", "US Mountain"],
  ["America/Los_Angeles", "US Pacific"],
  ["Europe/London", "London"],
  ["Europe/Berlin", "Frankfurt"],
  ["Asia/Tokyo", "Tokyo"],
  ["Asia/Hong_Kong", "Hong Kong"],
  ["Australia/Sydney", "Sydney"],
];

export interface ChartSettings {
  timeframe?: Timeframe;
  periods?: number;
  avgMode?: AvgMode;
  timeZone?: string;
}

/** Parse a persisted chart-settings JSON string, keeping only valid fields
 * so a stale/garbled stored value can never wedge the controls -- anything
 * invalid simply falls back to the defaults. */
export function parseChartSettings(json: string | null): ChartSettings {
  const out: ChartSettings = {};
  if (!json) return out;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    if (TIMEFRAMES.includes(raw.timeframe as Timeframe)) out.timeframe = raw.timeframe as Timeframe;
    if (
      typeof raw.periods === "number" &&
      Number.isInteger(raw.periods) &&
      raw.periods >= 6 &&
      raw.periods <= HARD_MAX_PERIODS
    ) {
      out.periods = raw.periods;
    }
    if (raw.avgMode === "single" || raw.avgMode === "double") out.avgMode = raw.avgMode;
    if (typeof raw.timeZone === "string" && raw.timeZone && TIMEZONES.some(([id]) => id === raw.timeZone)) {
      out.timeZone = raw.timeZone;
    }
  } catch {
    /* corrupt JSON -- defaults win */
  }
  return out;
}

export interface ChartView {
  root: HTMLElement;
  refresh(): Promise<void>;
}

export interface ChartViewOptions {
  /** Research band (admin-only server-side); omitted = the default band. */
  bandWidthUsd?: number;
  /** localStorage key for this instance's controls (default SETTINGS_KEY). */
  settingsKey?: string;
  /** Panel title (default "Ratio History"). */
  title?: string;
  /** Cap timeframe x periods to this many minutes. Research bands are
   * raw-only and the server caps one raw pull at 20,000 rows (~5.5 h), so
   * a wider window would come back truncated to its OLDEST slice. */
  maxWindowMinutes?: number;
}

export function buildChartView(onError: (message: string) => void, opts: ChartViewOptions = {}): ChartView {
  const settingsKey = opts.settingsKey ?? SETTINGS_KEY;
  // SW-6: restore any persisted control state; defaults match legacy
  // (1h/24 periods, single-marker mode -- legacy/dashboard.html line 1447).
  let stored: ChartSettings = {};
  try {
    stored = parseChartSettings(localStorage.getItem(settingsKey));
  } catch {
    /* storage unavailable (private mode) -- defaults win */
  }
  let timeframe: Timeframe = stored.timeframe ?? "1h";
  let periods = stored.periods ?? 24;
  let inFlight: AbortController | null = null;
  let avgMode: AvgMode = stored.avgMode ?? "single";
  let timeZone: string | undefined = stored.timeZone; // undefined = device local
  let lastBuckets: Bucket[] | null = null;

  function saveSettings(): void {
    try {
      localStorage.setItem(settingsKey, JSON.stringify({ timeframe, periods, avgMode, timeZone }));
    } catch {
      /* private mode / storage disabled -- settings just won't survive reload */
    }
  }

  // (2026-09-03) The role-tiered period ceiling (admin 200 / standard
  // 48h-derived caps, resolved via /v1/subscription/status) is gone with
  // the backend clamp: every entitled user gets the same HARD_MAX_PERIODS.

  /** Clamp the current selection to the UI ceiling and keep the input's
   * max attribute in step. */
  function maxPeriods(): number {
    const byWindow = opts.maxWindowMinutes
      ? Math.floor(opts.maxWindowMinutes / TIMEFRAME_MINUTES[timeframe])
      : HARD_MAX_PERIODS;
    return Math.max(1, Math.min(HARD_MAX_PERIODS, byWindow));
  }
  function applyPeriodCeiling(): void {
    const max = maxPeriods();
    periodsInput.max = String(max);
    if (periods > max) {
      periods = max;
      periodsInput.value = String(periods);
    }
  }

  const infoHigh = el("span", { class: "chart-info-value chart-info-high" }, ["--.--"]);
  const infoAvg = el("span", { class: "chart-info-value chart-info-avg" }, ["--.--"]);
  const infoLow = el("span", { class: "chart-info-value chart-info-low" }, ["--.--"]);
  const bars = el("div", { class: "bars-container" }, [el("div", { class: "chart-empty" }, ["Loading chart..."])]);
  const xLabels = el("div", { class: "x-labels" });
  const yAxis = el("div", { class: "y-axis" });
  const tooltip = el("div", { class: "chart-tooltip" });
  const resolutionNote = el("div", { class: "resolution-note" }, [""]);

  const elements: ChartElements = { bars, xLabels, yAxis, tooltip, infoHigh, infoAvg, infoLow };

  const periodsInput = el("input", {
    type: "number",
    class: "period-input",
    value: String(periods),
    min: "6",
    max: String(HARD_MAX_PERIODS),
  }) as HTMLInputElement;

  const timeframeButtons = TIMEFRAMES.map((tf) => {
    const btn = el("button", { class: "timeframe-btn" + (tf === timeframe ? " active" : "") }, [
      tf.toUpperCase(),
    ]);
    btn.addEventListener("click", () => {
      timeframe = tf;
      timeframeButtons.forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      applyPeriodCeiling();
      saveSettings();
      void doRefresh();
    });
    return btn;
  });

  periodsInput.addEventListener("change", () => {
    const parsed = parseInt(periodsInput.value, 10);
    const max = maxPeriods();
    periods = Number.isFinite(parsed) ? Math.min(max, Math.max(6, parsed)) : Math.min(24, max);
    periodsInput.value = String(periods);
    saveSettings();
    void doRefresh();
  });

  const avgToggleBtn = el("button", { class: "avg-toggle-btn active" }, [
    avgMode === "single" ? "Single" : "Double",
  ]);
  avgToggleBtn.addEventListener("click", () => {
    avgMode = avgMode === "single" ? "double" : "single";
    avgToggleBtn.textContent = avgMode === "single" ? "Single" : "Double";
    saveSettings();
    if (lastBuckets) renderChart(elements, lastBuckets, avgMode, timeZone);
  });

  // SW-9: timezone selector -- purely a client-side re-render (same data,
  // different wall-clock labels), so no refetch needed.
  const tzSelect = el(
    "select",
    { class: "tz-select" },
    TIMEZONES.map(([id, label]) => {
      const opt = el("option", { value: id }, [label]);
      if (id === (timeZone ?? "")) opt.selected = true;
      return opt;
    })
  );
  tzSelect.addEventListener("change", () => {
    timeZone = tzSelect.value || undefined;
    saveSettings();
    if (lastBuckets) renderChart(elements, lastBuckets, avgMode, timeZone);
  });

  function showPaywall(status: 401 | 402): void {
    lastBuckets = null;
    clear(bars);
    clear(xLabels);
    clear(yAxis);
    bars.append(
      el("div", { class: "chart-empty chart-paywall" }, [
        status === 401
          ? "Log in to view ratio history."
          : "Subscribe to unlock ratio history -- the free tier shows the live ratio only (ADR-0008).",
      ])
    );
    resolutionNote.textContent = "";
  }

  async function doRefresh(): Promise<void> {
    inFlight?.abort();
    // SW-21a: logged-out visitors can never get history (ADR-0008), so don't
    // ask -- the request was a guaranteed 401 on every load and every 60 s,
    // logged as a console error and into the SW-14 bug-report buffer.
    if (!isLoggedIn()) {
      showPaywall(401);
      return;
    }
    const controller = new AbortController();
    inFlight = controller;
    try {
      const data = await fetchChartData(timeframe, periods, controller.signal, opts.bandWidthUsd);
      if (controller.signal.aborted) return;
      lastBuckets = data.buckets;
      renderChart(elements, data.buckets, avgMode, timeZone);
      resolutionNote.textContent = data.truncated
        ? `resolution: ${data.resolution} (response truncated -- reduce periods)`
        : `resolution: ${data.resolution}`;
    } catch (err) {
      if (controller.signal.aborted) return;
      // /v1/history is gated by require_entitlement for EVERY caller, not
      // just anonymous ones (ADR-0008: "free users get the live ratio
      // only... all historical data access requires an active
      // subscription"). A 401/402 here isn't a transient failure worth a
      // flash-and-vanish error banner -- it's an expected, permanent state
      // for free/logged-out users, so it gets its own persistent message
      // in the chart panel itself instead.
      if (err instanceof ApiError && (err.status === 401 || err.status === 402)) {
        showPaywall(err.status);
        return;
      }
      // Backstop for the /v1/history limits: a malformed request (400) or
      // an account tripping the daily coverage / rate budget (429) gets a
      // friendly note in the chart panel rather than a broken chart or a
      // vanishing banner.
      if (err instanceof ApiError && (err.status === 400 || err.status === 429)) {
        lastBuckets = null;
        clear(bars);
        clear(xLabels);
        clear(yAxis);
        bars.append(
          el("div", { class: "chart-empty" }, [
            err.status === 429
              ? "History is temporarily rate-limited for this account -- please try again shortly."
              : "That time range is too wide -- reduce the number of periods to view history.",
          ])
        );
        resolutionNote.textContent = "";
        return;
      }
      onError(err instanceof Error ? err.message : String(err));
    }
  }

  const root = el("div", { class: "graph-panel" }, [
    el("div", { class: "chart-header" }, [
      el("div", { class: "chart-title" }, [opts.title ?? "Ratio History"]),
      el("div", { class: "chart-controls" }, [
        el("div", { class: "timeframe-selector" }, timeframeButtons),
        el("div", { class: "period-control" }, [
          el("span", { class: "period-label" }, ["Periods:"]),
          periodsInput,
        ]),
        avgToggleBtn,
        el("div", { class: "period-control" }, [
          el("span", { class: "period-label" }, ["Timezone:"]),
          tzSelect,
        ]),
      ]),
    ]),
    el("div", { class: "chart-wrapper" }, [
      el("div", { class: "chart-canvas" }, [yAxis, el("div", { class: "chart-area" }, [bars])]),
    ]),
    el("div", { class: "x-axis" }, [xLabels]),
    el("div", { class: "chart-info" }, [
      el("div", { class: "chart-info-item" }, [el("span", {}, ["HIGH:"]), infoHigh]),
      el("div", { class: "chart-info-item" }, [el("span", {}, ["AVG:"]), infoAvg]),
      el("div", { class: "chart-info-item" }, [el("span", {}, ["LOW:"]), infoLow]),
    ]),
    resolutionNote,
    tooltip,
  ]);

  // Bound the number input from the very first render.
  applyPeriodCeiling();

  /** Public refresh: fetch + render the chart. main.ts calls this at
   * startup, on every auth change, and on the 60s interval. */
  async function refresh(): Promise<void> {
    await doRefresh();
  }

  return {
    root,
    refresh,
  };
}
